import { XMarkIcon, BanknotesIcon, CalendarIcon, UserIcon } from '@heroicons/react/24/outline'

export default function WithdrawalDetailModal({ isOpen, onClose, withdrawal }) {
  if (!isOpen || !withdrawal) return null

  const getStatusLabel = (status) => {
    const labels = {
      'pending': '대기중',
      'reviewing': '검토중',
      'approved': '승인됨',
      'rejected': '거절됨',
      'completed': '완료됨'
    }
    return labels[status] || status
  }

  const getStatusColor = (status) => {
    const colors = {
      'pending': 'bg-yellow-100 text-yellow-800',
      'reviewing': 'bg-blue-100 text-blue-800',
      'approved': 'bg-green-100 text-green-800',
      'rejected': 'bg-red-100 text-red-800',
      'completed': 'bg-gray-100 text-gray-800'
    }
    return colors[status] || 'bg-gray-100 text-gray-800'
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <BanknotesIcon className="h-5 w-5" />
            출금 요청 상세정보
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {/* Status */}
        <div className="mb-6">
          <span className={`inline-flex px-3 py-1 text-sm font-semibold rounded-full ${getStatusColor(withdrawal.status)}`}>
            {getStatusLabel(withdrawal.status)}
          </span>
        </div>

        {/* Basic Info */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          <div>
            <h4 className="text-sm font-medium text-gray-700 mb-2">요청 정보</h4>
            <div className="space-y-2">
              <div>
                <span className="text-sm text-gray-600">요청 ID:</span>
                <span className="ml-2 text-sm font-medium">#{withdrawal.id}</span>
              </div>
              <div>
                <span className="text-sm text-gray-600">통화:</span>
                <span className="ml-2 text-sm font-medium">
                  {withdrawal.currency === 'krw' ? '원화' : '토큰'}
                </span>
              </div>
              <div>
                <span className="text-sm text-gray-600">금액:</span>
                <span className="ml-2 text-sm font-medium">
                  {Number(withdrawal.amount).toLocaleString()}
                  {withdrawal.currency === 'krw' ? '원' : '토큰'}
                </span>
              </div>
              <div>
                <span className="text-sm text-gray-600">신청일:</span>
                <span className="ml-2 text-sm font-medium">
                  {new Date(withdrawal.createdAt).toLocaleString('ko-KR')}
                </span>
              </div>
            </div>
          </div>

          <div>
            <h4 className="text-sm font-medium text-gray-700 mb-2">사용자 정보</h4>
            <div className="space-y-2">
              <div>
                <span className="text-sm text-gray-600">이름:</span>
                <span className="ml-2 text-sm font-medium">
                  {withdrawal.user?.name || '정보없음'}
                </span>
              </div>
              <div>
                <span className="text-sm text-gray-600">사용자명:</span>
                <span className="ml-2 text-sm font-medium">
                  @{withdrawal.user?.username || '정보없음'}
                </span>
              </div>
              <div>
                <span className="text-sm text-gray-600">연락처:</span>
                <span className="ml-2 text-sm font-medium">
                  {withdrawal.user?.contact || '없음'}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Withdrawal Info */}
        <div className="mb-6">
          <h4 className="text-sm font-medium text-gray-700 mb-2">출금 정보</h4>
          <div className="bg-gray-50 p-4 rounded-lg">
            {withdrawal.currency === 'krw' && withdrawal.withdrawalInfo ? (
              <div className="space-y-2">
                <div>
                  <span className="text-sm text-gray-600">은행명:</span>
                  <span className="ml-2 text-sm font-medium">
                    {withdrawal.withdrawalInfo.bankName}
                  </span>
                </div>
                <div>
                  <span className="text-sm text-gray-600">계좌번호:</span>
                  <span className="ml-2 text-sm font-medium">
                    {withdrawal.withdrawalInfo.accountNumber}
                  </span>
                </div>
                <div>
                  <span className="text-sm text-gray-600">예금주:</span>
                  <span className="ml-2 text-sm font-medium">
                    {withdrawal.withdrawalInfo.accountHolder}
                  </span>
                </div>
              </div>
            ) : withdrawal.currency === 'token' && withdrawal.withdrawalInfo ? (
              <div>
                <span className="text-sm text-gray-600">지갑 주소:</span>
                <span className="ml-2 text-sm font-medium break-all">
                  {withdrawal.withdrawalInfo.walletAddress}
                </span>
              </div>
            ) : (
              <p className="text-sm text-gray-500">출금 정보가 없습니다.</p>
            )}
          </div>
        </div>

        {/* Processing Info */}
        {withdrawal.processedByAdmin && (
          <div className="mb-6">
            <h4 className="text-sm font-medium text-gray-700 mb-2">처리 정보</h4>
            <div className="bg-blue-50 p-4 rounded-lg space-y-2">
              <div>
                <span className="text-sm text-gray-600">처리자:</span>
                <span className="ml-2 text-sm font-medium">
                  {withdrawal.processedByAdmin.name || '관리자'} 
                  (@{withdrawal.processedByAdmin.username})
                </span>
              </div>
              <div>
                <span className="text-sm text-gray-600">처리일:</span>
                <span className="ml-2 text-sm font-medium">
                  {new Date(withdrawal.updatedAt).toLocaleString('ko-KR')}
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Admin Notes */}
        {withdrawal.adminNotes && (
          <div className="mb-6">
            <h4 className="text-sm font-medium text-gray-700 mb-2">관리자 메모</h4>
            <div className="bg-yellow-50 p-4 rounded-lg">
              <p className="text-sm text-gray-700 whitespace-pre-wrap">
                {withdrawal.adminNotes}
              </p>
            </div>
          </div>
        )}

        {/* Close Button */}
        <div className="flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300"
          >
            닫기
          </button>
        </div>
      </div>
    </div>
  )
}